import { useCallback, useEffect, useMemo, useState } from 'react';
import { Building2, Loader2, Search, X } from 'lucide-react';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/components/ui/Toast';
import {
  editorApi,
  type EditorInstitutionOption,
  type EditorInstitutionShareRow,
  type EditorSharedContentType,
} from '@/api/editor.api';
import { InstitutionStyledSearchInput } from '@/components/institution/InstitutionStyledSearchInput';

type Props = {
  contentType: EditorSharedContentType;
  contentId: string;
  contentTitle: string;
  onClose: () => void;
};

export function ShareWithInstitutionsModal({ contentType, contentId, contentTitle, onClose }: Props) {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [institutions, setInstitutions] = useState<EditorInstitutionOption[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [existingShares, setExistingShares] = useState<EditorInstitutionShareRow[]>([]);

  const loadShares = useCallback(async () => {
    setLoading(true);
    try {
      const shares = await editorApi.listShares(contentType, contentId);
      setExistingShares(shares);
      setSelectedIds(new Set(shares.map((s) => s.institutionId)));
    } catch {
      setExistingShares([]);
    } finally {
      setLoading(false);
    }
  }, [contentType, contentId]);

  useEffect(() => {
    void loadShares();
  }, [loadShares]);

  useEffect(() => {
    const t = window.setTimeout(async () => {
      setSearching(true);
      try {
        const rows = await editorApi.searchInstitutions(search.trim() || undefined);
        setInstitutions(rows);
      } catch {
        setInstitutions([]);
      } finally {
        setSearching(false);
      }
    }, search ? 280 : 0);
    return () => window.clearTimeout(t);
  }, [search]);

  const selectedInstitutions = useMemo(() => {
    const fromSearch = institutions.filter((i) => selectedIds.has(i.id));
    const knownIds = new Set(fromSearch.map((i) => i.id));
    const fromShares = existingShares
      .filter((s) => selectedIds.has(s.institutionId) && !knownIds.has(s.institutionId))
      .map((s) => ({
        id: s.institutionId,
        name: s.institutionName,
        email: s.institutionEmail,
        subdomain: s.subdomain,
        logo: s.logo,
      }));
    return [...fromShares, ...fromSearch];
  }, [institutions, selectedIds, existingShares]);

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const updated = await editorApi.replaceShares(contentType, contentId, [...selectedIds]);
      setExistingShares(updated);
      addToast({
        type: 'success',
        title: 'Partage enregistré',
        description: `${updated.length} établissement(s) ont accès à ce contenu.`,
      });
      onClose();
    } catch (e: unknown) {
      addToast({
        type: 'error',
        title: 'Enregistrement impossible',
        description:
          e && typeof e === 'object' && 'message' in e ? String((e as { message: string }).message) : 'Erreur',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} size="lg" ariaLabelledBy="editor-share-institutions-title">
      <ModalHeader>
        <h2 id="editor-share-institutions-title" className="text-lg font-semibold text-gray-900 dark:text-white">
          Partager avec des établissements
        </h2>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          {contentTitle} — uniquement les entreprises disposant d’un compte LyrraOne.
        </p>
      </ModalHeader>
      <ModalBody className="space-y-4">
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-7 w-7 animate-spin text-primary-600" aria-hidden />
          </div>
        ) : (
          <>
            <InstitutionStyledSearchInput
              placeholder="Rechercher un établissement (nom, e-mail, sous-domaine)…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="max-h-48 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-800">
              {searching && (
                <p className="px-3 py-4 text-sm text-gray-500 flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Recherche…
                </p>
              )}
              {!searching && institutions.length === 0 && (
                <p className="px-3 py-4 text-sm text-gray-500">Aucun établissement trouvé.</p>
              )}
              {!searching &&
                institutions.map((inst) => {
                  const checked = selectedIds.has(inst.id);
                  return (
                    <label
                      key={inst.id}
                      className="flex cursor-pointer items-center gap-3 px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(inst.id)}
                        className="rounded border-gray-300"
                      />
                      <Building2 className="h-4 w-4 shrink-0 text-gray-400" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-gray-900 dark:text-white">
                          {inst.name}
                        </span>
                        <span className="block truncate text-xs text-gray-500">{inst.email}</span>
                      </span>
                    </label>
                  );
                })}
            </div>
            {selectedInstitutions.length > 0 && (
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Établissements sélectionnés ({selectedInstitutions.length})
                </p>
                <ul className="flex flex-wrap gap-2">
                  {selectedInstitutions.map((inst) => (
                    <li
                      key={inst.id}
                      className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-2.5 py-1 text-xs font-medium text-primary-800 dark:bg-primary-900/30 dark:text-primary-200"
                    >
                      {inst.name}
                      <button
                        type="button"
                        className="rounded p-0.5 hover:bg-primary-100 dark:hover:bg-primary-800/50"
                        aria-label={`Retirer ${inst.name}`}
                        onClick={() => toggle(inst.id)}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Les apprenants et la publication publique ne sont pas disponibles pour les comptes éditeur. Seuls les
              établissements listés pourront consulter ce contenu dans leur espace LyrraOne.
            </p>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button type="button" variant="ghost" disabled={saving} onClick={onClose}>
          Annuler
        </Button>
        <Button type="button" disabled={saving || loading} onClick={() => void save()}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Enregistrer le partage'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
